import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { AutoPagination, DataTable, useDataTable } from "@/components/table";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  SearchBar,
  Typography,
} from "@/components/ui";
import useIsMobile from "@/components/ui/sidebar/hooks/use-is-mobile";
import AddCustomerRelationDialog from "@/features/root/components/add-customer-relation-dialog";
import DeleteSupplierRelationDialog from "@/features/root/components/delete-supplier-relation-dialog";
import { useSupplierMappingQuery } from "@/features/root/hooks/use-supplier-mapping-query";
import type { SuppliersMapping } from "@/types/root/suppliers-mapping.type";

export const Route = createFileRoute("/_protected/root/customer-relation")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    customerId: search.customerId as string,
  }),
});

const getStatusLabel = (status: "active" | "inactive") => {
  return status === "active" ? "已啟用" : "未啟用";
};

const columns: ColumnDef<SuppliersMapping>[] = [
  {
    accessorKey: "factoryName",
    header: "中心廠名稱",
  },
  {
    accessorKey: "supplierName",
    header: "供應商全名",
  },
  {
    accessorKey: "supplierShortName",
    header: "供應商簡稱",
  },
  {
    accessorKey: "venderCode",
    header: "供應商代號",
  },
  {
    accessorKey: "status",
    header: "狀態",
    cell: ({ row }) => (
      <Badge variant={row.original.status === "active" ? "default" : "secondary"}>
        {getStatusLabel(row.original.status)}
      </Badge>
    ),
  },
  {
    id: "delete",
    header: "刪除",
    cell: ({ row }) => <DeleteSupplierRelationDialog supplierMapping={row.original} />,
  },
];

function RouteComponent() {
  const { customerId } = Route.useSearch();
  const { data: customers } = useSupplierMappingQuery(customerId);

  const isMobile = useIsMobile();

  const { table, globalFilter, setGlobalFilter } = useDataTable<SuppliersMapping>({
    data: customers ?? [],
    columns,
    options: {
      paginationOptions: true,
      globalFilterOptions: true,
    },
  });

  return (
    <section>
      <Card>
        <CardHeader>
          <Typography variant="h2">關聯關係管理</Typography>
          <CardDescription>管理與您的供應商之間的關聯</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="w-full md:w-auto">
              <SearchBar
                className="grow"
                placeholder="請輸入供應商名稱、代號..."
                value={globalFilter}
                onChange={setGlobalFilter}
              />
            </span>

            <AddCustomerRelationDialog customerId={customerId} />
          </div>

          <DataTable table={table} />

          <AutoPagination table={table} showTotalCount showSizeChanger={!isMobile} />
        </CardContent>
      </Card>
    </section>
  );
}
